import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { LockInfo } from '../../types/DeploymentRecord';
import { tagStyle } from '../theme/statusTokens';
import { isEscalatedRollbackLock } from '../lib/locks';
import { formatTimestamp, timeAgo } from '../lib/format';

export interface LockBadgeProps {
  lock: LockInfo;
  /** Reference time for age/escalation; injectable for tests. Defaults to now. */
  now?: number;
}

const sourceHue = (source?: string): string => {
  switch (source) {
    case 'rollback':
      return '#d93a3a'; // red — auto-rollback placed this
    case 'incident':
      return '#dd5b00'; // hot orange
    default:
      return '#787774'; // grey — manual / unknown
  }
};

const sourceLabel = (source?: string): string => {
  switch (source) {
    case 'rollback':
      return 'Rollback lock';
    case 'incident':
      return 'Incident lock';
    case 'manual':
      return 'Locked';
    default:
      return 'Locked';
  }
};

/**
 * Inline lock-state badge for a service row (DEVOPS-1633). Shows source + age;
 * rollback locks older than the escalation threshold get a heavier red border
 * so operators can't ignore a stale rollback lock. Hover reveals reason, owner,
 * and timestamps. Display only — the self-service unlock button (M6-03b) renders
 * into this row once it exists.
 */
const LockBadge: React.FC<LockBadgeProps> = ({ lock, now = Date.now() }) => {
  const theme = useTheme();
  const escalated = isEscalatedRollbackLock(lock, now);
  const hue = sourceHue(lock.lock_source);
  const style = tagStyle(hue, theme.palette.mode);
  const age = timeAgo(lock.locked_at);

  const tooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {sourceLabel(lock.lock_source)}
        {escalated ? ' — needs attention' : ''}
      </Typography>
      {lock.lock_reason && (
        <Typography variant="caption">Reason: {lock.lock_reason}</Typography>
      )}
      {lock.locked_by && (
        <Typography variant="caption">By: {lock.locked_by}</Typography>
      )}
      {lock.locked_at && (
        <Typography variant="caption">Locked: {formatTimestamp(lock.locked_at)}</Typography>
      )}
      {lock.expires_at && (
        <Typography variant="caption">Expires: {formatTimestamp(lock.expires_at)}</Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow>
      <Chip
        icon={<LockIcon sx={{ fontSize: 13, color: `${style.text} !important` }} />}
        label={age ? `${sourceLabel(lock.lock_source)} · ${age}` : sourceLabel(lock.lock_source)}
        size="small"
        sx={{
          backgroundColor: style.bg,
          color: style.text,
          border: escalated ? `2px solid ${hue}` : `1px solid ${style.border}`,
          fontWeight: escalated ? 700 : 600,
          fontSize: '0.7rem',
          height: 20,
          '& .MuiChip-label': { px: 0.75 },
          ...(escalated
            ? { boxShadow: `0 0 0 2px ${style.bg}`, letterSpacing: 0.2 }
            : {}),
        }}
      />
    </Tooltip>
  );
};

export default LockBadge;
